import { useEffect, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { NAV_ITEMS } from './navigation'
import { useWorkspace } from '@/providers/WorkspaceProvider'
import { useAccounts } from '@/features/accounts/useAccounts'
import { Badge } from '@/components/ui'

interface SidebarProps {
  open: boolean
  onClose: () => void
}

/** "1 conta precisa ser reconectada" / "3 contas precisam ser reconectadas". */
function alertText(count: number): string {
  return count === 1
    ? '1 conta precisa ser reconectada'
    : `${count} contas precisam ser reconectadas`
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { workspaceId } = useWorkspace()
  const { data: accounts } = useAccounts(workspaceId)
  const needsAttention = (accounts ?? []).filter((account) => account.status !== 'active').length

  return (
    <nav aria-label="Navegação principal" className="flex-1 space-y-1 px-3 py-4">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon
        const showAlert = item.to === '/contas' && needsAttention > 0
        // O rótulo visível é curto; o nome acessível continua completo — e o
        // alerta entra nele para o badge não ser anunciado como um "1" solto.
        const fullName = item.accessibleLabel ?? item.label
        const accessibleName = showAlert
          ? `${fullName}: ${alertText(needsAttention)}`
          : item.accessibleLabel

        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={onNavigate}
            title={item.description}
            aria-label={accessibleName}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--surface-inset)] hover:text-[var(--text-primary)]',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={cn(
                    'size-5 shrink-0',
                    isActive ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]',
                  )}
                  aria-hidden="true"
                />
                {/* `truncate` é só uma rede de segurança: com rótulos curtos
                    (ex.: "Contas") a largura sobra e nada é cortado. */}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {showAlert && (
                  <Badge tone="warning" dot title={alertText(needsAttention)} className="shrink-0">
                    {needsAttention}
                    <span className="sr-only">
                      {needsAttention === 1
                        ? 'conta precisa ser reconectada'
                        : 'contas precisam ser reconectadas'}
                    </span>
                  </Badge>
                )}
              </>
            )}
          </NavLink>
        )
      })}
    </nav>
  )
}

function Brand() {
  return (
    <div className="flex h-16 items-center gap-2.5 border-b border-[var(--border-subtle)] px-5">
      <span
        aria-hidden="true"
        className="flex size-8 items-center justify-center rounded-lg bg-[var(--accent)] text-sm font-bold text-[var(--accent-contrast)]"
      >
        SH
      </span>
      <span className="text-base font-semibold tracking-tight text-[var(--text-primary)]">
        SocialHub
      </span>
    </div>
  )
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const drawerRef = useRef<HTMLDivElement>(null)

  // Esc fecha o menu móvel — saída sempre disponível (heurística #3).
  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    drawerRef.current?.focus()
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  return (
    <>
      {/* Desktop: navegação persistente */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-[var(--border-subtle)] bg-[var(--surface-card)] lg:flex">
        <Brand />
        <NavList />
        <p className="px-5 pb-4 text-xs text-[var(--text-muted)]">
          Pressione <kbd className="rounded border border-[var(--border-strong)] px-1">C</kbd> para
          criar
        </p>
      </aside>

      {/* Mobile: drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-[var(--surface-overlay)]"
            onClick={onClose}
            aria-hidden="true"
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
            tabIndex={-1}
            className="relative flex h-full w-72 max-w-[85vw] flex-col bg-[var(--surface-card)] shadow-xl"
            style={{ animation: 'sh-fade-in 150ms ease-out' }}
          >
            <div className="flex items-center justify-between border-b border-[var(--border-subtle)] pr-2">
              <Brand />
              <button
                type="button"
                onClick={onClose}
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-inset)]"
              >
                <X className="size-5" aria-hidden="true" />
                <span className="sr-only">Fechar menu</span>
              </button>
            </div>
            <NavList onNavigate={onClose} />
          </div>
        </div>
      )}
    </>
  )
}
